import { Big } from 'big.js';
import {
  getReadonlyListItemCurrency,
  getReadonlyListItemDate,
  getReadonlyListItemId,
  getReadonlyListItemNumber,
  getReadonlyListItemPill,
  getReadonlyListItemString,
  getReadonlyListItemTranslatedString,
  getReadonlyListItemYesOrNo,
} from '@interxfi/app-components/components/readonly-list/util';
import type { ReadonlyListItemRecord } from '@interxfi/design-system/components/list';
import type {
  IxcSweepsClearingV1BalancesReconcileDetails,
  IxcSweepsClearingV1BalancesReconcileDrift,
  IxcSweepsClearingV1BalancesReconcileOrder,
  IxcSweepsClearingV1BalancesReconcileSegment,
  IxcSweepsClearingV1DividendsCalculateDetails,
  IxcSweepsClearingV1Metrics,
  IxcSweepsClearingV1SweepFundsSpec,
  IxcSweepsClearingV1SweepPayment,
  IxcSweepsClearingV1SweepsTransaction,
} from '@interxfi/types';
import { getDateFormatter } from '@interxfi/utilities/date/formatting';
import { getIntlNumberFormat } from '@interxfi/utilities/intl/number';
import {
  getBooleanFromUndefinedOrNullable,
  getNumberOrUndefinedFromNullable,
  getNumericStringOrUndefinedFromNullable,
} from '@interxfi/utilities/object/access';
import { getAssetDetailPath } from '../../asset/asset-routing';
import { getRegistrationAccountDetailPath } from '../../registration/account/registration-account-routing';
import { getSweepTargetDetailPath } from '../target/sweep-target-routing';
import { SweepOrderTypePill } from './order/sweep-order-type-pill';

const dateFormatter = getDateFormatter('en-US', {
  day: 'numeric',
  month: 'numeric',
  year: 'numeric',
});

const timeFormatter = getDateFormatter('en-US', {
  hour: 'numeric',
  minute: 'numeric',
  second: 'numeric',
  timeZoneName: 'short',
});

const numberFormatter = getIntlNumberFormat('en-US');

export function getSweepPaymentSummary(data: IxcSweepsClearingV1SweepPayment | undefined) {
  if (!data) {
    return undefined;
  }

  try {
    return [
      numberFormatter.format(new Big(data.amount || 0).toNumber()),
      'paid on',
      dateFormatter.format(data.paymentTime as unknown as Date),
      'at',
      timeFormatter.format(data.paymentTime as unknown as Date),
      'for sweeps ending on',
      data.endDate,
    ].join(' ');
  } catch {
    return undefined;
  }
}

export function getSweepsBalanceReconcileSegmentListItems(data: IxcSweepsClearingV1BalancesReconcileSegment | undefined, prefix = '') {
  return {
    stepDate: getReadonlyListItemDate(`${prefix}stepDate`, 'Step Date', data?.stepDate, 'date_iso'),
    availableCashDelta: getReadonlyListItemCurrency(
      `${prefix}availableCashDelta`,
      'Available Cash Delta',
      getNumericStringOrUndefinedFromNullable(data, 'availableCashDelta'),
    ),
    suspected: getReadonlyListItemYesOrNo(`${prefix}suspected`, 'Suspected', getBooleanFromUndefinedOrNullable(data, 'suspected')),
  } as const satisfies ReadonlyListItemRecord;
}

export function getSweepsBalanceReconcileOrderListItems(data: IxcSweepsClearingV1BalancesReconcileOrder | undefined, prefix = '') {
  return {
    orderType: getReadonlyListItemTranslatedString(`${prefix}orderType`, 'Order Type', {
      namespace: 'ixcSweepsClearingV1',
      enumName: 'IxcSweepsClearingV1OrderType',
      value: data?.orderType,
    }),
    orderTypePill: getReadonlyListItemPill(
      `${prefix}orderTypePill`,
      'Order Type',
      data?.orderType ? <SweepOrderTypePill orderType={data.orderType} /> : undefined,
    ),
    sweepTargetId: getReadonlyListItemId(
      `${prefix}sweepTargetId`,
      'Sweep Target ID',
      data?.sweepTargetId,
      getSweepTargetDetailPath(data?.sweepTargetId),
    ),
    assetId: getReadonlyListItemId(`${prefix}assetId`, 'Asset ID', data?.assetId, getAssetDetailPath(data?.assetId)),
    quantity: getReadonlyListItemNumber(`${prefix}quantity`, 'Quantity', data?.quantity),
    sweepsCutoffTimestamp: getReadonlyListItemDate(
      `${prefix}sweepsCutoffTimestamp`,
      'Sweeps Cutoff Timestamp',
      data?.sweepsCutoffTimestamp,
      'timestamp_iso_ms',
    ),
  } as const satisfies ReadonlyListItemRecord;
}

export function getSweepsBalanceReconcileDriftListItems(data: IxcSweepsClearingV1BalancesReconcileDrift | undefined, prefix = '') {
  return {
    startDate: getReadonlyListItemDate(`${prefix}startDate`, 'Start Date', data?.startDate, 'date_iso'),
    settledCutoffs: getReadonlyListItemNumber(`${prefix}settledCutoffs`, 'Settled Cutoffs', getNumberOrUndefinedFromNullable(data, 'settledCutoffs')),
    latestDelta: getReadonlyListItemCurrency(`${prefix}latestDelta`, 'Latest Delta', getNumericStringOrUndefinedFromNullable(data, 'latestDelta')),
    minDelta: getReadonlyListItemCurrency(`${prefix}minDelta`, 'Min Delta', getNumericStringOrUndefinedFromNullable(data, 'minDelta')),
    maxDelta: getReadonlyListItemCurrency(`${prefix}maxDelta`, 'Max Delta', getNumericStringOrUndefinedFromNullable(data, 'maxDelta')),
  } as const satisfies ReadonlyListItemRecord;
}

export function getSweepBalancesReconcileDetailListItems(data: IxcSweepsClearingV1BalancesReconcileDetails | undefined, prefix = '') {
  return {
    segments: data?.segments?.map((segment, index) => getSweepsBalanceReconcileSegmentListItems(segment, `${prefix}segments.${index}.`)),
    orders: data?.orders?.map((order, index) => getSweepsBalanceReconcileOrderListItems(order, `${prefix}orders.${index}.`)),
    settledHorizonDate: getReadonlyListItemDate(`${prefix}settledHorizonDate`, 'Settled Horizon Date', data?.settledHorizonDate, 'date_iso'),
    paidThroughDate: getReadonlyListItemDate(`${prefix}paidThroughDate`, 'Paid Through Date', data?.paidThroughDate, 'date_iso'),
    storedAvailableCash: getReadonlyListItemCurrency(
      `${prefix}storedAvailableCash`,
      'Stored Available Cash',
      getNumericStringOrUndefinedFromNullable(data, 'storedAvailableCash'),
    ),
    expectedAvailableCash: getReadonlyListItemCurrency(
      `${prefix}expectedAvailableCash`,
      'Expected Available Cash',
      getNumericStringOrUndefinedFromNullable(data, 'expectedAvailableCash'),
    ),
    availableCashDelta: getReadonlyListItemCurrency(
      `${prefix}availableCashDelta`,
      'Available Cash Delta',
      getNumericStringOrUndefinedFromNullable(data, 'availableCashDelta'),
    ),
    sweptCashDelta: getReadonlyListItemCurrency(
      `${prefix}sweptCashDelta`,
      'Swept Cash Delta',
      getNumericStringOrUndefinedFromNullable(data, 'sweptCashDelta'),
    ),
    settledSweptDelta: getReadonlyListItemCurrency(
      `${prefix}settledSweptDelta`,
      'Settled Swept Delta',
      getNumericStringOrUndefinedFromNullable(data, 'settledSweptDelta'),
    ),
    pendingSweptDelta: getReadonlyListItemCurrency(
      `${prefix}pendingSweptDelta`,
      'Pending Swept Delta',
      getNumericStringOrUndefinedFromNullable(data, 'pendingSweptDelta'),
    ),
    rowsUpdated: getReadonlyListItemNumber(`${prefix}rowsUpdated`, 'Rows Updated', getNumberOrUndefinedFromNullable(data, 'rowsUpdated')),
    correctionApplied: getReadonlyListItemYesOrNo(
      `${prefix}correctionApplied`,
      'Correction Applied',
      getBooleanFromUndefinedOrNullable(data, 'correctionApplied'),
    ),
    refused: getReadonlyListItemYesOrNo(`${prefix}refused`, 'Refused', getBooleanFromUndefinedOrNullable(data, 'refused')),
    drift: data?.drift ? getSweepsBalanceReconcileDriftListItems(data.drift, `${prefix}drift.`) : undefined,
  } as const satisfies ReadonlyListItemRecord;
}

export function getSweepPaymentListItems(data: IxcSweepsClearingV1SweepPayment | undefined, prefix = '') {
  return {
    endDate: getReadonlyListItemDate(`${prefix}endDate`, 'End Date', data?.endDate, 'date_iso'),
    paymentTime: getReadonlyListItemDate(`${prefix}paymentTime`, 'Payment Time', data?.paymentTime, 'timestamp_iso_s'),
    amount: getReadonlyListItemNumber(`${prefix}amount`, 'Amount', data?.amount),
    summary: getReadonlyListItemString(`${prefix}summary`, 'Sweep Payment', getSweepPaymentSummary(data)),
  } as const satisfies ReadonlyListItemRecord;
}

export function getSweepFundsSpecListItems(data: IxcSweepsClearingV1SweepFundsSpec | undefined, prefix = '') {
  return {
    sweptBalance: getReadonlyListItemNumber(`${prefix}sweptBalance`, 'Swept Balance', data?.sweptBalance),
    dividends: getReadonlyListItemNumber(`${prefix}dividends`, 'Dividends', data?.dividends),
    dividendDate: getReadonlyListItemDate(`${prefix}dividendDate`, 'Dividend Date', data?.dividendDate, 'date_iso'),
  } as const satisfies ReadonlyListItemRecord;
}

export function getSweepMetricsListItems(data: IxcSweepsClearingV1Metrics | undefined, prefix = '') {
  return {
    total: getReadonlyListItemNumber(`${prefix}total`, 'Total', data?.total ?? 0),
    pending: getReadonlyListItemNumber(`${prefix}pending`, 'Pending', data?.pending ?? 0),
    succeeded: getReadonlyListItemNumber(`${prefix}succeeded`, 'Succeeded', data?.succeeded ?? 0),
    errored: getReadonlyListItemNumber(`${prefix}errored`, 'Errored', data?.errored ?? 0),
    failed: getReadonlyListItemNumber(`${prefix}failed`, 'Failed', data?.failed ?? 0),
    expectedTotal: getReadonlyListItemNumber(`${prefix}expectedTotal`, 'Expected Total', data?.expectedTotal ?? 0),
    receivedCount: getReadonlyListItemNumber(`${prefix}receivedCount`, 'Received Count', data?.receivedCount ?? 0),
  } as const satisfies ReadonlyListItemRecord;
}

export function getSweepsDividendCalculateDetails(data: IxcSweepsClearingV1DividendsCalculateDetails | undefined, prefix = '') {
  return {
    dividendChange: getReadonlyListItemNumber(`${prefix}dividendChange`, 'Dividend Change', data?.dividendChange),
    sweptBalanceChange: getReadonlyListItemCurrency(`${prefix}sweptBalanceChange`, 'Swept Balance Change', data?.sweptBalanceChange),
    executedAt: getReadonlyListItemDate(`${prefix}executedAt`, 'Executed At', data?.executedAt, 'timestamp_iso_ms'),
  } as const satisfies ReadonlyListItemRecord;
}

export function getClearingSweepsTransactionListItems(data: IxcSweepsClearingV1SweepsTransaction | undefined, prefix = '') {
  return {
    adapterOrderId: getReadonlyListItemId(`${prefix}adapterOrderId`, 'Adapter Order ID', data?.adapterOrderId),
    accountId: getReadonlyListItemId(`${prefix}accountId`, 'Account ID', data?.accountId, getRegistrationAccountDetailPath(data?.accountId)),
    confirmTime: getReadonlyListItemDate(`${prefix}confirmTime`, 'Confirm Time', data?.confirmTime, 'timestamp_iso_ms'),
    symbol: getReadonlyListItemString(`${prefix}symbol`, 'Symbol', data?.symbol),
    cusip: getReadonlyListItemString(`${prefix}cusip`, 'CUSIP', data?.cusip),
    assetId: getReadonlyListItemId(`${prefix}assetId`, 'Asset ID', data?.assetId, getAssetDetailPath(data?.assetId)),
    quantity: getReadonlyListItemNumber(`${prefix}quantity`, 'Quantity', getNumericStringOrUndefinedFromNullable(data, 'quantity')),
    orderType: getReadonlyListItemTranslatedString(`${prefix}orderType`, 'Order Type', {
      namespace: 'ixcSweepsClearingV1',
      enumName: 'IxcSweepsClearingV1OrderType',
      value: data?.orderType,
    }),
    orderTypePill: getReadonlyListItemPill(
      `${prefix}sweepOrderTypePill`,
      'Order Type',
      data?.orderType ? <SweepOrderTypePill orderType={data.orderType} /> : undefined,
    ),
    fillPrice: getReadonlyListItemCurrency(`${prefix}fillPrice`, 'Fill Price', getNumericStringOrUndefinedFromNullable(data, 'fillPrice')),
    netAmount: getReadonlyListItemCurrency(`${prefix}netAmount`, 'Net Amount', getNumericStringOrUndefinedFromNullable(data, 'netAmount')),
  } as const satisfies ReadonlyListItemRecord;
}
